import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type StateService from 'garaje/services/state';
import type { Resource, Floor, Neighborhood, Amenity } from 'garaje/types/maps/live-map/LiveMapServiceV2';

interface ResourcePopupComponentArgs {
  resource: Resource;
  amenities?: Amenity[];
  floors: Floor[];
  neighborhoods: Neighborhood[];
  onNeighborhoodsClick: (neighborhoods: Neighborhood[]) => void;
}

export default class ResourcePopup extends Component<ResourcePopupComponentArgs> {
  @service declare state: StateService;

  @tracked isAmenitiesExpanded = false;
  @tracked showNeighborhoodPopup = false;

  getResourceTypeLabel(): string {
    const resourceType = getIdFromURN(this.args.resource.resourceTypeId) as string;
    if (!resourceType) return '';

    return resourceType.replace(/_/g, ' ');
  }

  get hasEmployeeInfo(): boolean {
    return this.args.resource.reservedForProfileData !== null || this.args.resource.assignedProfileData !== null;
  }

  get headerLabel(): string {
    const employeeName =
      this.args.resource.reservedForProfileData?.name || this.args.resource.assignedProfileData?.name;

    return this.hasEmployeeInfo ? employeeName || `Assigned ${this.getResourceTypeLabel()}` : this.args.resource.name;
  }

  get subHeaderLabel(): string {
    return this.hasEmployeeInfo ? `${this.args.resource.name}, ${this.floor?.name}` : (this.floor?.name ?? '');
  }

  get floor(): Floor | undefined {
    return this.args.floors.find((floor) => floor.id === this.args.resource.floorId);
  }

  get neighborhood(): Neighborhood | undefined {
    if (!this.args.resource.neighborhoodId) return undefined;
    return this.args.neighborhoods.find((neighborhood) => neighborhood.id === this.args.resource.neighborhoodId);
  }

  get amenitiesToDisplay(): string[] {
    const { resource } = this.args;

    if (this.isAmenitiesExpanded) {
      return this.getAmenityName(resource.amenities);
    }

    return this.getAmenityName(resource.amenities.slice(0, 2));
  }

  getAmenityName(amenityIds: string[]): string[] {
    if (!this.args.amenities) return [];

    return amenityIds.map((amenityId) => {
      const amenity = this.args.amenities!.find((amenity) => amenity.id === amenityId);
      if (amenity) {
        return amenity.name;
      }
      return '';
    });
  }

  @action
  toggleIsAmenitiesExpanded(e: Event): void {
    this.isAmenitiesExpanded = !this.isAmenitiesExpanded;
    e.stopPropagation();
  }

  @action
  onNeighborhoodClick(neighborhood: Neighborhood[], e: Event): void {
    e.stopPropagation();
    this.args.onNeighborhoodsClick(neighborhood);
    this.showNeighborhoodPopup = true;
  }
}
